import React from 'react';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import { formatTimeHhMmSsInput } from '../../utils/formatters/timeFormatters';
import type { StoreLocation } from '../../types/services/auth';

const errorBorderSx = {
  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
    borderColor: 'error.main',
    borderWidth: '1px',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'error.main',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'error.main',
  },
};

type VendingSalesFormHeaderProps = {
  control: any;
  errors: any;
  reportType: 'vending_sales' | 'vending_inventory';
  storeLocations?: StoreLocation[];
};

const VendingSalesFormHeader: React.FC<VendingSalesFormHeaderProps> = React.memo(({
  control,
  errors,
  reportType,
  storeLocations = [],
}) => {
  const isInventoryReport = reportType === 'vending_inventory';
  const headerFieldSx = {
    width: '100%',
    minWidth: 0,
    '& .MuiInputBase-root': {
      fontFamily: 'inherit',
      bgcolor: 'rgba(255,255,255,0.45)',
      minWidth: 0,
    },
    '& .MuiInputBase-input': {
      fontFamily: 'inherit',
      fontSize: isInventoryReport ? 13 : 14,
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'inherit',
      fontSize: isInventoryReport ? 13 : 14,
    },
  };

  const headerInputStyle = {
    height: isInventoryReport ? 28 : 30,
    padding: isInventoryReport ? '0 6px' : '0 8px',
  };

  return (
    <Box
      sx={{
        display: 'block',
        mb: 0,
        color: '#2b2825',
        fontFamily: 'inherit',
      }}
    >
    <Box sx={{ height: 25, mb: 1 }} />

    <Typography
      sx={{
        textAlign: 'center',
        fontFamily: 'inherit',
        fontSize: 24,
        fontWeight: 800,
        lineHeight: 1.2,
        mt: 1,
      }}
    >
      {isInventoryReport ? 'INVENTORY REPORT' : 'SALES REPORT'}
    </Typography>

    {!isInventoryReport && (
      <Typography
        sx={{
          textAlign: 'center',
          fontFamily: 'inherit',
          fontSize: 20,
          lineHeight: 1.2,
          mt: 0.5,
          mb: 1.5,
        }}
      >
        YESTERDAY
      </Typography>
    )}

    <Box
      sx={{
        display: 'grid',
        width: '100%',
        minWidth: 0,
        gridTemplateColumns: {
          xs: '1fr 1fr',
          sm: isInventoryReport
            ? 'repeat(2, minmax(0, 1fr))'
            : 'repeat(3, minmax(0, 1fr))',
          md: isInventoryReport
            ? 'minmax(112px, 1.15fr) minmax(74px, 0.72fr) minmax(82px, 0.78fr) minmax(78px, 0.74fr) minmax(118px, 1.16fr)'
            : 'minmax(118px, 1.25fr) minmax(84px, 0.82fr) minmax(84px, 0.82fr) minmax(118px, 1.12fr)',
        },
        gap: 1,
        alignItems: 'center',
        mb: 3,
        '& > *': {
          minWidth: 0,
        },
      }}
    >
      <Controller
        name="shift_date"
        control={control}
        render={({ field }) => (
          <TextField
            label="Date"
            size="small"
            type="date"
            required
            {...field}
            value={field.value ?? ''}
            error={!!errors?.shift_date}
            helperText={errors?.shift_date?.message || ''}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { style: headerInputStyle },
            }}
            sx={{
              ...(errors?.shift_date ? errorBorderSx : {}),
              ...headerFieldSx,
            }}
          />
        )}
      />

      {isInventoryReport && (
        <Controller
          name="shift_start_time"
          control={control}
          render={({ field }) => (
            <TextField
              label="Time"
              size="small"
              required
              {...field}
              value={field.value ?? ''}
              onChange={(event) => field.onChange(formatTimeHhMmSsInput(event.target.value))}
              error={!!errors?.shift_start_time}
              helperText={errors?.shift_start_time?.message || ''}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { style: headerInputStyle },
              }}
              sx={{
                ...(errors?.shift_start_time ? errorBorderSx : {}),
                ...headerFieldSx,
              }}
            />
          )}
        />
      )}

      <Controller
        name="parsed_data.final_json.retailer"
        control={control}
        render={({ field }) => (
          <TextField
            label="Retailer"
            size="small"
            required
            {...field}
            error={!!errors?.parsed_data?.final_json?.retailer}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { style: headerInputStyle },
            }}
            sx={{
              ...(errors?.parsed_data?.final_json?.retailer ? errorBorderSx : {}),
              ...headerFieldSx,
            }}
          />
        )}
      />

      <Controller
        name="parsed_data.final_json.terminal"
        control={control}
        render={({ field }) => (
          <TextField
            label="Terminal"
            size="small"
            {...field}
            error={!!errors?.parsed_data?.final_json?.terminal}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { style: headerInputStyle },
            }}
            sx={{
              ...(errors?.parsed_data?.final_json?.terminal ? errorBorderSx : {}),
              ...headerFieldSx,
            }}
          />
        )}
      />

      <Controller
        name="location_key"
        control={control}
        render={({ field }) => (
          <TextField
            label="Location"
            size="small"
            select
            {...field}
            value={field.value ?? ''}
            error={!!errors?.location_key}
            helperText={errors?.location_key?.message || ''}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { style: headerInputStyle },
            }}
            sx={{
              ...(errors?.location_key ? errorBorderSx : {}),
              ...headerFieldSx,
              '& .MuiSelect-select': {
                height: isInventoryReport ? 28 : 30,
                minHeight: `${isInventoryReport ? 28 : 30}px !important`,
                display: 'flex',
                alignItems: 'center',
                padding: isInventoryReport ? '0 26px 0 6px' : '0 30px 0 8px',
              },
            }}
          >
            <MenuItem value="">Select location</MenuItem>
            {storeLocations.length === 0 && (
              <MenuItem value="" disabled>No locations</MenuItem>
            )}
            {storeLocations.map((location) => (
              <MenuItem key={location.id} value={location.location_key}>
                {location.location_name}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </Box>
  </Box>
  );
});

VendingSalesFormHeader.displayName = 'VendingSalesFormHeader';

export default VendingSalesFormHeader;
